/**
 * FORMULÁRIO DE PREMISSAS MACROECONÔMICAS
 * 
 * Permite editar:
 * - Taxa Selic (Risk-free rate)
 * - Inflação (IPCA)
 * - Crescimento do PIB
 * - Prêmio de Risco Brasil (EMBI+)
 * - Prêmio de Risco de Mercado
 */

import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Divider,
  Typography,
  Alert,
  Chip,
} from '@mui/material';
import { TrendingUp, ShowChart } from '@mui/icons-material';

import { PercentageInput } from './CommonInputs';
import type { MacroAssumptions } from '../../types/valuation';

// ============================================================================
// TYPES
// ============================================================================

export interface MacroAssumptionsFormProps {
  values: MacroAssumptions;
  onChange: (values: MacroAssumptions) => void;
  disabled?: boolean;
}

// ============================================================================
// VALORES PADRÃO E LIMITES
// ============================================================================

const DEFAULTS: MacroAssumptions = {
  taxaSelic: 0.1175,      // 11,75%
  inflacao: 0.045,        // 4,5%
  crescimentoPIB: 0.025,  // 2,5%
  premioRiscoBrasil: 0.035, // 3,5%
  premioRiscoMercado: 0.08, // 8%
};

const LIMITS = {
  taxaSelic: { min: 0, max: 30, tooltip: 'Taxa Selic histórica: 2-30%' },
  inflacao: { min: 0, max: 20, tooltip: 'Inflação histórica: 0-20%' },
  crescimentoPIB: { min: -10, max: 15, tooltip: 'PIB histórico: -10% a +15%' },
  premioRiscoBrasil: { min: 0, max: 10, tooltip: 'EMBI+ Brasil: 0-10%' },
  premioRiscoMercado: { min: 0, max: 20, tooltip: 'Prêmio de risco de mercado: 5-15%' },
};

// ============================================================================
// COMPONENT
// ============================================================================

export function MacroAssumptionsForm({
  values,
  onChange,
  disabled = false,
}: MacroAssumptionsFormProps) {
  
  const handleChange = (field: keyof MacroAssumptions) => (value: number) => {
    onChange({
      ...values,
      [field]: value,
    });
  };

  const handleReset = () => {
    onChange(DEFAULTS);
  };

  // Validações
  const taxaSelicAlta = values.taxaSelic > 0.15; // > 15%
  const inflacaoAlta = values.inflacao > 0.06; // > 6%
  const pibBaixo = values.crescimentoPIB < 0.015; // < 1.5%

  return (
    <Card>
      <CardHeader
        title="Premissas Macroeconômicas"
        subheader="Indicadores econômicos para cálculo do WACC"
        avatar={<TrendingUp />}
        action={
          <Chip
            label="Reset Padrões"
            onClick={handleReset}
            disabled={disabled}
            size="small"
            variant="outlined"
          />
        }
      />
      <Divider />
      <CardContent>
        {/* Alertas de validação */}
        {(taxaSelicAlta || inflacaoAlta || pibBaixo) && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="body2" gutterBottom>
              <strong>Atenção:</strong> Premissas fora do intervalo usual:
            </Typography>
            <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
              {taxaSelicAlta && <li>Selic acima de 15% (cenário restritivo)</li>}
              {inflacaoAlta && <li>Inflação acima de 6% (acima da meta)</li>}
              {pibBaixo && <li>PIB abaixo de 1,5% (crescimento fraco)</li>}
            </ul>
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Taxa Selic */}
          <Grid item xs={12} md={6}>
            <PercentageInput
              label="Taxa Selic (Risk-free)"
              value={values.taxaSelic}
              onChange={handleChange('taxaSelic')}
              min={LIMITS.taxaSelic.min}
              max={LIMITS.taxaSelic.max}
              helpText={LIMITS.taxaSelic.tooltip}
              disabled={disabled}
              fullWidth
              helperText="Taxa básica de juros (Banco Central)"
            />
          </Grid>

          {/* Inflação */}
          <Grid item xs={12} md={6}>
            <PercentageInput
              label="Inflação (IPCA)"
              value={values.inflacao}
              onChange={handleChange('inflacao')}
              min={LIMITS.inflacao.min}
              max={LIMITS.inflacao.max}
              helpText={LIMITS.inflacao.tooltip}
              disabled={disabled}
              fullWidth
              helperText="Índice Nacional de Preços ao Consumidor Amplo"
            />
          </Grid>

          {/* Crescimento PIB */}
          <Grid item xs={12} md={6}>
            <PercentageInput
              label="Crescimento do PIB"
              value={values.crescimentoPIB}
              onChange={handleChange('crescimentoPIB')}
              min={LIMITS.crescimentoPIB.min}
              max={LIMITS.crescimentoPIB.max}
              helpText={LIMITS.crescimentoPIB.tooltip}
              disabled={disabled}
              fullWidth
              helperText="Taxa de crescimento real do PIB brasileiro"
            />
          </Grid>

          {/* Prêmio de Risco Brasil */}
          <Grid item xs={12} md={6}>
            <PercentageInput
              label="Prêmio de Risco Brasil (EMBI+)"
              value={values.premioRiscoBrasil}
              onChange={handleChange('premioRiscoBrasil')}
              min={LIMITS.premioRiscoBrasil.min}
              max={LIMITS.premioRiscoBrasil.max}
              helpText={LIMITS.premioRiscoBrasil.tooltip}
              disabled={disabled}
              fullWidth
              helperText="Emerging Markets Bond Index Plus Brasil"
            />
          </Grid>

          {/* Prêmio de Risco de Mercado */}
          <Grid item xs={12} md={6}>
            <PercentageInput
              label="Prêmio de Risco de Mercado"
              value={values.premioRiscoMercado}
              onChange={handleChange('premioRiscoMercado')}
              min={LIMITS.premioRiscoMercado.min}
              max={LIMITS.premioRiscoMercado.max}
              helpText={LIMITS.premioRiscoMercado.tooltip}
              disabled={disabled}
              fullWidth
              helperText="Diferencial entre retorno esperado do mercado e risk-free"
            />
          </Grid>
        </Grid>

        {/* Informações adicionais */}
        <Box sx={{ mt: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
            <ShowChart fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
            <strong>Impacto no Valuation:</strong>
          </Typography>
          <Typography variant="caption" color="text.secondary" component="div">
            • Selic e Risco Brasil aumentam o WACC → reduzem o valuation
            <br />
            • Inflação afeta projeções reais vs nominais
            <br />
            • PIB indica crescimento potencial do mercado
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
}
